import { BADGE_TONES } from "../../../components/ui";
import { buildThemeClasses } from "../theme";
import { BOOKING_STATUS_META, WEEKDAYS_ES, type BookingStatus, type Booking, type TimeBlock } from "../services/bookingsService";

// ===========================================================================
// Shared calendar grids — the month grid and the week/day time grid, plus the
// pure date-key helpers they rely on. Used by both the Agenda calendar
// (sections/agenda/AgendaCalendar) and the per-agent Reuniones calendar
// (sections/ReunionesLive) so there's a single implementation.
//
// Date keys are "yyyy-mm-dd" strings; math is done tz-agnostically via UTC noon
// so it never drifts across DST. Wall-clock minutes are computed by the caller
// in the business / agent timezone.
// ===========================================================================

export type View = "month" | "week" | "day";

export const pad = (n: number) => String(n).padStart(2, "0");

export const keyToUTC = (key: string) => new Date(`${key}T12:00:00Z`);
export const utcToKey = (d: Date) =>
  `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
export function addDaysKey(key: string, n: number): string {
  const d = keyToUTC(key);
  d.setUTCDate(d.getUTCDate() + n);
  return utcToKey(d);
}
export const weekdayOfKey = (key: string) => keyToUTC(key).getUTCDay(); // 0=Sun
export function startOfWeekKey(key: string): string {
  const diff = (weekdayOfKey(key) + 6) % 7; // Monday-start
  return addDaysKey(key, -diff);
}
export function monthGridKeys(key: string): string[] {
  const [y, m] = key.split("-").map(Number);
  const start = startOfWeekKey(`${y}-${pad(m)}-01`);
  return Array.from({ length: 42 }, (_, i) => addDaysKey(start, i));
}
export function weekKeys(key: string): string[] {
  const start = startOfWeekKey(key);
  return Array.from({ length: 7 }, (_, i) => addDaysKey(start, i));
}

// --- minute-of-day formatting ----------------------------------------------
export function formatMinShort(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${pad(h)}:${pad(m)}`;
}
export function formatMinInput(min: number): string {
  return formatMinShort(min);
}
export function addMinutesToInput(time: string, minutes: number): string {
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + minutes;
  return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
}

// --- unified calendar event -------------------------------------------------
export interface CalEvent {
  id: string;
  kind: "booking" | "block" | "external";
  title: string;
  startKey: string;
  startMin: number; // minutes-of-day in the rendered timezone
  endMin: number;
  status?: BookingStatus;
  booking?: Booking;
  block?: TimeBlock;
  /** For read-only Google events not owned by the CRM. */
  htmlLink?: string | null;
}

// ---------------------------------------------------------------------------
// Month grid — 6×7 day cells with up to 3 event chips each.
// ---------------------------------------------------------------------------
export function MonthGrid({
  refKey,
  todayKey,
  eventsByDay,
  tc,
  onPickDay,
  onSelect,
}: {
  refKey: string;
  todayKey: string;
  eventsByDay: Record<string, CalEvent[]>;
  tc: ReturnType<typeof buildThemeClasses>;
  onPickDay: (key: string) => void;
  onSelect: (e: CalEvent) => void;
}) {
  const keys = monthGridKeys(refKey);
  const month = Number(refKey.split("-")[1]);
  const dayNames = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  return (
    <div className="p-3">
      <div className="grid grid-cols-7">
        {dayNames.map((d) => (
          <div key={d} className={`pb-2 text-center text-xs font-semibold ${tc.textSecondary}`}>
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {keys.map((k) => {
          const inMonth = Number(k.split("-")[1]) === month;
          const isToday = k === todayKey;
          const dayEvents = eventsByDay[k] || [];
          return (
            <div
              key={k}
              onClick={() => onPickDay(k)}
              className={`min-h-[96px] cursor-pointer rounded-lg border p-1.5 transition-colors border-line hover:border-primary-500 ${inMonth ? "" : "opacity-40"}`}
            >
              <div className="mb-1 flex items-center justify-between">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                    isToday ? "bg-primary-500 text-white" : tc.text
                  }`}
                >
                  {Number(k.split("-")[2])}
                </span>
                {dayEvents.length > 3 && (
                  <span className={`text-[10px] ${tc.textSecondary}`}>+{dayEvents.length - 3}</span>
                )}
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((ev) => (
                  <button
                    key={ev.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(ev);
                    }}
                    className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium ${
                      ev.kind === "block"
                        ? "bg-gray-400/20 text-gray-500 line-through decoration-1"
                        : ev.kind === "external"
                          ? "bg-sky-500/15 text-sky-600 dark:text-sky-400"
                          : BADGE_TONES[BOOKING_STATUS_META[ev.status!]?.tone ?? "neutral"].soft
                    } ${ev.status === "cancelled" ? "opacity-50 line-through" : ""}`}
                    title={ev.title}
                  >
                    {ev.kind === "block" ? "🔒 " : ev.kind === "external" ? "📅 " : ""}
                    {formatMinShort(ev.startMin)} {ev.title}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Time grid (week / day) — absolute-positioned events over an hour ruler.
// ---------------------------------------------------------------------------
export function TimeGrid({
  keys,
  dayStartH,
  dayEndH,
  todayKey,
  eventsByDay,
  isWithinAvailability,
  tc,
  onEmptyClick,
  onSelect,
}: {
  keys: string[];
  dayStartH: number;
  dayEndH: number;
  todayKey: string;
  eventsByDay: Record<string, CalEvent[]>;
  isWithinAvailability: (key: string, minute: number) => boolean;
  tc: ReturnType<typeof buildThemeClasses>;
  onEmptyClick: (key: string, minute: number) => void;
  onSelect: (e: CalEvent) => void;
}) {
  const hours = Array.from({ length: dayEndH - dayStartH }, (_, i) => dayStartH + i);
  const HOUR_PX = 56;
  const totalMin = (dayEndH - dayStartH) * 60;
  const gridHeight = hours.length * HOUR_PX;
  const minToTop = (min: number) => ((min - dayStartH * 60) / totalMin) * gridHeight;

  return (
    <div className="overflow-x-auto p-3">
      <div className="min-w-[640px]">
        {/* day headers */}
        <div className="grid" style={{ gridTemplateColumns: `56px repeat(${keys.length}, 1fr)` }}>
          <div />
          {keys.map((k) => {
            const d = keyToUTC(k);
            const isToday = k === todayKey;
            return (
              <div key={k} className="px-1 pb-2 text-center">
                <div className={`text-xs font-medium ${tc.textSecondary}`}>{WEEKDAYS_ES[d.getUTCDay()]}</div>
                <div
                  className={`mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                    isToday ? "bg-primary-500 text-white" : tc.text
                  }`}
                >
                  {d.getUTCDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* grid body */}
        <div className="grid" style={{ gridTemplateColumns: `56px repeat(${keys.length}, 1fr)` }}>
          {/* hour labels */}
          <div className="relative" style={{ height: gridHeight }}>
            {hours.map((h, i) => (
              <div
                key={h}
                className={`absolute -translate-y-1/2 pr-2 text-right text-[11px] ${tc.textSecondary}`}
                style={{ top: i * HOUR_PX, right: 0, width: "100%" }}
              >
                {pad(h)}:00
              </div>
            ))}
          </div>

          {/* day columns */}
          {keys.map((k) => {
            const dayEvents = eventsByDay[k] || [];
            return (
              <div
                key={k}
                className="relative border-l border-line"
                style={{ height: gridHeight }}
              >
                {/* hour cells (click to create) + availability shading */}
                {hours.map((h, i) => {
                  const available = isWithinAvailability(k, h * 60);
                  return (
                    <div
                      key={h}
                      onClick={() => onEmptyClick(k, h * 60)}
                      className={`absolute left-0 right-0 cursor-pointer border-b border-line ${available ? "" : "bg-subtle/50"} hover:bg-primary-500/5`}
                      style={{ top: i * HOUR_PX, height: HOUR_PX }}
                    />
                  );
                })}

                {/* events */}
                {dayEvents.map((ev, idx) => {
                  const top = minToTop(ev.startMin);
                  const height = Math.max(18, minToTop(ev.endMin) - top);
                  const cancelled = ev.status === "cancelled";
                  return (
                    <button
                      key={ev.id}
                      onClick={() => onSelect(ev)}
                      className={`absolute overflow-hidden rounded-md border-l-2 px-1.5 py-1 text-left text-[11px] shadow-sm transition-transform hover:z-10 hover:scale-[1.02] ${
                        ev.kind === "block"
                          ? "border-gray-400 bg-gray-400/20 text-gray-500"
                          : ev.kind === "external"
                            ? "border-sky-500 bg-sky-500/15 text-sky-600 dark:text-sky-400"
                            : `border-primary-500 ${BADGE_TONES[BOOKING_STATUS_META[ev.status!]?.tone ?? "primary"].soft}`
                      } ${cancelled ? "opacity-50 line-through" : ""}`}
                      style={{
                        top,
                        height,
                        left: `${4 + (idx % 2) * 4}px`,
                        right: `${4 + ((idx + 1) % 2) * 4}px`,
                      }}
                      title={`${ev.title} · ${formatMinShort(ev.startMin)}–${formatMinShort(ev.endMin)}`}
                    >
                      <span className="block truncate font-semibold">
                        {ev.kind === "block" ? "🔒 " : ev.kind === "external" ? "📅 " : ""}
                        {ev.title}
                      </span>
                      <span className="block truncate opacity-80">
                        {formatMinShort(ev.startMin)}–{formatMinShort(ev.endMin)}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
